import { Component, computed, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdvancedEquipmentConditions, GearHighlightConfig, JewelleryFilter } from '../../../../models/filter-models';
import { JewelleryType, Rarity } from '../../../../models/enums';
import { FilterService } from '../../../../services/filter.service';
import { AutocompleteComponent } from '../../../../common/autocomplete/autocomplete.component';
import { BaseType } from '../../../../models/item-data-models';
import { SelectComponent, SelectOptionComponent } from '../../../../common/select/select.component';
import { BaseComponent } from '../../../../common/base.component';
import { ItemDataService } from '../../../../services/item-data.service';
import { GenerationHelperService } from '../../../../services/generators/generation-helper.service';
import { AdvancedEquipmentConditionsComponent } from '../../advanced-equipment-conditions/advanced-equipment-conditions.component';
import { MyEquipmentHighlightsComponent } from '../../my-equipment-highlights/my-equipment-highlights.component';
import { SelectRarityComponent } from '../../../../common/select/select-rarity/select-rarity.component';
import { TooltipWrapperComponent } from "../../../../common/tooltip-wrapper/tooltip-wrapper.component";

@Component({
  selector: 'app-jewellery-filter',
  imports: [FormsModule, AutocompleteComponent, SelectComponent, SelectOptionComponent,
    SelectRarityComponent, AdvancedEquipmentConditionsComponent, MyEquipmentHighlightsComponent, TooltipWrapperComponent],
  templateUrl: './jewellery-filter.component.html',
  styleUrl: './jewellery-filter.component.scss'
})
export class JewelleryFilterComponent extends BaseComponent implements OnInit {
  jewellery = input.required<JewelleryFilter>();
  index = input.required<number>();
  remove = output<number>();

  hasAdvancedConditions = 0;
  advancedConditionsOpen = false;
  hasHighlightConfig = 0;
  highlightConfigOpen = false;

  jewelleryTypes: BaseType[] = [];

  private jewelleryTypeVersion = signal(0);

  jewelleryBaseTypeAutocompleteOptions = computed(() => {
    this.jewelleryTypeVersion();
    return this.generationHelper.getMyJewelleryBaseTypeAutocompleteOptions(this.jewellery().jewelleryType);
  });

  constructor(
    private filterService: FilterService,
    private itemDataService: ItemDataService,
    private generationHelper: GenerationHelperService,
  ) {
    super();

    this.jewelleryTypes = [
      { name: 'Amulets', image: this.itemDataService.getItemData().find(i => i.itemType === 'Amulets')!.baseTypes[0].image! },
      { name: 'Rings', image: this.itemDataService.getItemData().find(i => i.itemType === 'Rings')!.baseTypes[0].image! },
      { name: 'Belts', image: this.itemDataService.getItemData().find(i => i.itemType === 'Belts')!.baseTypes[0].image! },
    ];

    if (this.poe1) {
      this.jewelleryTypes.push({ name: 'Talismans', image: this.itemDataService.getItemData().find(i => i.itemType === 'Talismans')!.baseTypes[0].image! });
    }
  }

  ngOnInit(): void {
    this.hasAdvancedConditions = this.getNumberOfAdvancedConditions();
    this.hasHighlightConfig = this.getNumberOfHighlightConfig();
  }

  toggleAdvancedOptionsOpen() {
    this.advancedConditionsOpen = !this.advancedConditionsOpen;
  }

  toggleHighlightConfigOpen() {
    this.highlightConfigOpen = !this.highlightConfigOpen;
  }

  getNumberOfAdvancedConditions() {
    return AdvancedEquipmentConditions.hasChanges(this.jewellery());
  }

  getNumberOfHighlightConfig() {
    return GearHighlightConfig.hasChanges(this.jewellery().highlightConfig);
  }

  toggleShowWeapon() {
    this.jewellery().show = !this.jewellery().show;
    if (!this.jewellery().show) {
      this.advancedConditionsOpen = false;
      this.highlightConfigOpen = false;
    }
    this.updateFilter();
  }

  removeWeaponType() {
    this.remove.emit(this.index());
  }

  jewelleryTypeChanged(jewelleryType: string) {
    const jewellery = this.jewellery();
    jewellery.jewelleryType = jewelleryType as JewelleryType;
    jewellery.jewelleryBaseTypes = ['All'];
    this.jewelleryTypeVersion.update(v => v + 1);
    this.updateFilter();
  }

  jewelleryBaseTypesChanged(jewelleryBaseTypes: string[]) {
    this.jewellery().jewelleryBaseTypes = jewelleryBaseTypes;
    this.updateFilter();
  }

  minUnidentifiedTierChanged(unidentifiedTier: number | null) {
    const jewellery = this.jewellery();
    if (jewellery.rarity === Rarity.Normal && unidentifiedTier) {
      jewellery.rarity = Rarity.Magic;
    }

    if (jewellery.highlightConfig?.rareMinTier && unidentifiedTier && jewellery.highlightConfig?.rareMinTier < unidentifiedTier) {
      jewellery.highlightConfig.rareMinTier = unidentifiedTier;
    }
    if (jewellery.highlightConfig?.magicMinTier && unidentifiedTier && jewellery.highlightConfig?.magicMinTier < unidentifiedTier) {
      jewellery.highlightConfig.magicMinTier = unidentifiedTier;
    }

    this.updateFilter();
  }

  minRarityChanged(rarity: Rarity) {
    const jewellery = this.jewellery();
    if (rarity === Rarity.Normal) {
      jewellery.unidentifiedTier = undefined;
      jewellery.highlightConfig.regularNormal = true;
      jewellery.highlightConfig.regularMagic = true;
      jewellery.highlightConfig.magic = true;
    }

    if (jewellery.rarity === Rarity.Magic) {
      jewellery.highlightConfig.regularNormal = true;
      jewellery.highlightConfig.regularMagic = true;
      jewellery.highlightConfig.magic = true;
    }

    if (jewellery.rarity === Rarity.Rare) {
      jewellery.highlightConfig.regularNormal = false;
      jewellery.highlightConfig.regularMagic = false;
      jewellery.highlightConfig.magic = false;
    }

    this.hasHighlightConfig = this.getNumberOfHighlightConfig();

    this.updateFilter();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }
}
